import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useLocation, useNavigate } from 'react-router-dom';

import { useUploadConceptGroupFileSync } from '@verantos-dev/feature-epi-chat';

import { prefetchDraftReviewData } from '../../../lib/api/prefetchDraftReviewData';
import { projectPageKeys } from '../../../lib/api/queryKeys';

// Same cap ConceptsFileUpload enforces on the create-modal dropzone.
const CONCEPT_GROUP_FILE_MAX_BYTES = 4 * 1024 * 1024;

/**
 * The draft's "Change file" flow (OR-6288 pass 3): "Change file" opens the OS
 * picker directly — no in-view upload step, no confirm modal. The picked file
 * uploads in the background (the review area shows a skeleton), the sync upload
 * mints the new draft (superseding this one server-side), the new draft's
 * queries are prefetched so its route renders without a skeleton flash, and the
 * view navigates onto it.
 */
export const useDraftFileReplace = ({
  projectId,
  previewId,
  /** The name the user will publish — rides the replacement upload. */
  currentName,
}: {
  projectId: string;
  previewId: string;
  currentName: string;
}) => {
  const { formatMessage } = useIntl();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  // Keeps the skeleton up between the upload finishing and the new draft's
  // route landing (its data is prefetched in that window — no flash).
  const [isFinalizingReplace, setIsFinalizingReplace] = useState(false);
  const { mutate: uploadFile, isPending: uploadPending } = useUploadConceptGroupFileSync();
  const isReplacing = uploadPending || isFinalizingReplace;
  const chatSessionId = new URLSearchParams(location.search).get('chat');

  // A re-upload mints a NEW draft (new ``previewId``) but reuses this mounted
  // component (a param change, not a remount) — drop stale pick feedback.
  useEffect(() => {
    setFileError(null);
    setIsFinalizingReplace(false);
  }, [previewId]);

  const onFilePicked = (picked: File | null) => {
    if (!picked || isReplacing) return;
    if (picked.size > CONCEPT_GROUP_FILE_MAX_BYTES) {
      setFileError(formatMessage({ id: 'conceptGroup.fileUpload.errorSize' }));
      return;
    }
    setFileError(null);
    uploadFile(
      {
        projectId,
        file: picked,
        sessionId: chatSessionId,
        name: currentName.trim() || undefined,
        // UC-4.1: the new draft supersedes this one — the backend discards it.
        replacesPreviewId: previewId,
      },
      {
        onSuccess: async (created) => {
          // Hold the skeleton through the prefetch below.
          setIsFinalizingReplace(true);
          // The superseded Draft row must vanish without a manual refresh.
          queryClient.invalidateQueries({
            queryKey: projectPageKeys.artifactsForProject(projectId),
          });
          queryClient.invalidateQueries({
            queryKey: projectPageKeys.activityForProject(projectId),
          });
          // Warm the new draft's queries so the destination renders
          // immediately — no skeleton flash after ours disappears.
          await prefetchDraftReviewData(queryClient, {
            conceptGroupId: created.conceptGroupId,
            previewId: created.previewId,
          });
          // OR-6288 finding #8: stay on the route family this view was mounted
          // on — canonical ``/concept-group/:id`` when a stable id exists, the
          // legacy preview shim otherwise.
          const onLegacyPreviewRoute = location.pathname.includes('/concept-group-preview/');
          const target =
            !onLegacyPreviewRoute && created.conceptGroupId
              ? `/projects/${projectId}/concept-group/${created.conceptGroupId}${location.search}`
              : `/projects/${projectId}/concept-group-preview/${created.previewId}${location.search}`;
          navigate(target, { replace: false });
        },
        onError: () => {
          // A failed replace (e.g. invalid/unreadable file → 422) must not fall
          // back silently: drop the skeleton and surface an error on the
          // source-file bar so the user knows to pick a different file. The old
          // draft is untouched server-side (the supersede runs only on success).
          setIsFinalizingReplace(false);
          setFileError(formatMessage({ id: 'conceptGroup.fileUpload.errorReplace' }));
        },
      }
    );
  };

  const openPicker = () => fileInputRef.current?.click();

  return { fileInputRef, onFilePicked, openPicker, isReplacing, fileError };
};
